import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ImageOff, ShieldCheck } from "lucide-react";
import { STATUSES, divisionOf, formatPrice, imageUrl } from "../../lib/api";
import { ShareListing } from "./ShareListing";

export const StatusBadge = ({ status, className = "" }) => {
  const s = STATUSES[status] || STATUSES.available;
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-[0.12em] ${s.cls} ${className}`} data-testid="asset-status-badge">
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {s.label}
    </span>
  );
};

export const VerifiedBadge = ({ asset, className = "", short = false }) => (asset?.verified ? (
  <span title="Verified by Selby Asset Group: details and photos reviewed before listing" className={`inline-flex items-center gap-1.5 whitespace-nowrap border border-[rgba(212,175,55,0.45)] px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-[0.12em] text-[#e9cf7a] ${className}`} data-testid="asset-verified-badge">
    <ShieldCheck className="w-3 h-3" /> {short ? "Verified" : "Verified by Selby Asset Group"}
  </span>
) : null);

export const AssetCard = ({ asset, index = 0 }) => {
  const div = divisionOf(asset.division);
  const cover = asset.images?.[0];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      <ShareListing asset={asset} compact className="absolute top-4 right-4 z-10" />
      <Link to={`/inventory/item/${asset.id}`} className="group block bg-[var(--ink-2)] border hairline hover:border-[rgba(212,175,55,0.45)] transition-colors duration-500" data-testid={`asset-card-${asset.id}`}>
        <div className="relative aspect-[4/3] overflow-hidden bg-[var(--ink-3)]">
          {cover ? (
            <img src={imageUrl(cover)} alt={asset.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
          ) : (
            <div className="w-full h-full grid place-items-center text-slate-500"><ImageOff className="w-8 h-8" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14]/80 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 right-16 flex flex-wrap gap-2">
            <StatusBadge status={asset.status} className="bg-[#0a0d14]/70 backdrop-blur" />
            <VerifiedBadge asset={asset} short className="bg-[#0a0d14]/70 backdrop-blur" />
          </div>
          <span className="absolute bottom-4 left-4 font-mono text-[0.65rem] tracking-[0.18em] uppercase text-slate-200">{div?.short} &middot; {asset.category || "General"}</span>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-display text-2xl leading-tight text-slate-50 group-hover:text-gold transition-colors duration-300">{asset.title}</h3>
            <ArrowUpRight className="w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-slate-400">
            {asset.year && <span>{asset.year}</span>}
            {asset.make && <span>{asset.make}</span>}
            {asset.usage && <span>{asset.usage}</span>}
            {asset.location && <span>{asset.location}</span>}
          </div>
          <div className="flex items-end justify-between pt-4 border-t hairline">
            <span className="text-lg font-semibold text-slate-50" data-testid="asset-card-price">{formatPrice(asset.price)}</span>
            <span className="font-mono text-[0.65rem] text-slate-400">#{asset.stock_number}</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
